/**
 * SuspiciousActivityCard — surfaces audit-log entries where
 * `result == "denied"` in the last 7 days, grouped by actor.
 *
 * Rendered at the top of the Audit log panel (founder + sales_manager).
 * Helps the operator spot a sales_person repeatedly attempting
 * founder-only writes, or a trial_user fishing for endpoints, etc.
 *
 * Lazy-loads /api/admin/audit-log with the result=denied filter as
 * soon as the parent panel mounts. Quiet on empty (renders nothing).
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, UserX, ChevronRight } from "lucide-react";
import api from "../../lib/api";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export const SuspiciousActivityCard = ({ onClickActor }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull the most recent 500 entries; client-side filters down
        // to result=denied in the last 7 days. (The server filters
        // by action prefix and policy tag but not by `result`; we keep
        // the server filter surface narrow.)
        const { data } = await api.get("/admin/audit-log", { params: { limit: 500 } });
        if (cancelled) return;
        const cutoff = Date.now() - SEVEN_DAYS_MS;
        const denied = (data.items || []).filter((e) => {
          if (e.result !== "denied") return false;
          const ts = new Date(e.at).getTime();
          return Number.isFinite(ts) && ts >= cutoff;
        });
        setEntries(denied);
      } catch {
        /* silent — widget is non-essential */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (entries.length === 0) return null;

  // Group by actor_email (fall back to actor_user_id)
  const grouped = entries.reduce((acc, e) => {
    const key = e.actor_email || e.actor_user_id || "unknown";
    if (!acc[key]) acc[key] = { actor: key, role: e.actor_role, count: 0, actions: new Set(), latest: e.at };
    acc[key].count += 1;
    acc[key].actions.add(e.action);
    if (e.at > acc[key].latest) acc[key].latest = e.at;
    return acc;
  }, {});

  const rows = Object.values(grouped)
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  return (
    <div
      className="bg-amber-50 border-2 border-amber-300 p-5"
      data-testid="suspicious-activity-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="ek-eyebrow text-amber-800 text-[11px]">Governance signal</div>
          <h3 className="ek-heading text-[15px] text-amber-900 mt-1">
            Suspicious activity — {entries.length} denied action{entries.length === 1 ? "" : "s"} in the last 7 days
          </h3>
          <p className="text-[12.5px] text-amber-900/80 mt-1 max-w-2xl font-light">
            Each row below shows a user who tried to perform an action their role doesn&rsquo;t permit. Investigate
            anything unexpected — a repeating pattern from the same actor may be a misunderstanding, a mistake, or
            (rarely) something more.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <button
            type="button"
            key={r.actor}
            onClick={() => onClickActor && onClickActor(r.actor)}
            className="w-full flex items-center justify-between gap-4 px-3 py-2 bg-white border border-amber-200 hover:border-amber-400 transition-colors text-left"
            data-testid={`suspicious-actor-${r.actor}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <UserX size={14} className="text-amber-700 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-zinc-800 truncate">{r.actor}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  role: <span className="font-mono">{r.role}</span>
                  {" · "}
                  attempted: <span className="font-mono">{Array.from(r.actions).slice(0, 3).join(", ")}{r.actions.size > 3 ? ` +${r.actions.size - 3}` : ""}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 bg-amber-200 text-amber-900 text-[12px] font-semibold tabular-nums">
                {r.count}
              </span>
              <ChevronRight size={14} className="text-amber-600" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SuspiciousActivityCard;
